import React, { useEffect, useState } from 'react';
import QuizQuestionCard from './QuizQuestionCard';
import MathText from './ui/MathText';
import CloseButton from './ui/CloseButton';
import { ClickToViewImage } from './ImageViewer';
import { useApiCaller } from '../utils/ApiUtilities';
import { getSimilarPracticeQuestionsUrl } from '../utils/UrlUtilities';

// Letters (e.g. "A,C") ↔ option keys ("option_a"). QuizQuestionCard reports selections as
// option keys; the API returns correct_option as comma-joined letters.
const letterOf = (optionKey) => (optionKey || '').slice(-1).toUpperCase();
const parseLetters = (correct) =>
    (correct || '').split(',').map((s) => s.trim().toUpperCase()).filter(Boolean);

const selectionLetters = (answer) => {
    if (Array.isArray(answer)) return answer.map(letterOf).sort();
    if (typeof answer === 'string' && answer) return [letterOf(answer)];
    return [];
};

const sameLetters = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);

// Read-only "correct answer" block for a single practice question, styled like the green
// reveal in Quiz Submission Details.
function CorrectAnswerReveal({ q, answer }) {
    const green = { backgroundColor: '#d1fae5', color: '#065f46', padding: '0.5rem', borderRadius: '0.25rem' };
    const correctLetters = parseLetters(q.correct_option);
    const userLetters = selectionLetters(answer);
    const answered = userLetters.length > 0;
    const isCorrect = answered && sameLetters(userLetters.slice().sort(), correctLetters.slice().sort());

    return (
        <div style={{ marginTop: '0.75rem', paddingTop: '0.6rem', borderTop: '1px solid #e5e7eb' }}>
            {answered && (
                <p style={{ fontSize: '0.85rem', marginBottom: '0.4rem', fontWeight: 600, color: isCorrect ? '#065f46' : '#991b1b' }}>
                    Your answer: {userLetters.join(', ')} {isCorrect ? '✓ Correct' : '✗ Incorrect'}
                </p>
            )}
            {q.question_type === 'TEXT' ? (
                <div style={green}>
                    <strong>Correct Answer: </strong>
                    {q.correct_option ? <MathText text={q.correct_option} /> : <span className="text-muted">— not set —</span>}
                </div>
            ) : (
                <div className="d-flex flex-column gap-1">
                    {correctLetters.length === 0 ? (
                        <div style={green}><strong>Correct Answer: </strong><span className="text-muted">— not set —</span></div>
                    ) : (
                        correctLetters.map((l) => (
                            <div key={l} style={green}>
                                <strong>{l}. </strong>
                                {q[`option_${l.toLowerCase()}`] ? <MathText text={q[`option_${l.toLowerCase()}`]} /> : null}
                            </div>
                        ))
                    )}
                </div>
            )}
            {(q.answer_explanation || q.explanation_image_url) && (
                <div style={{ marginTop: '0.5rem', padding: '0.75rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '0.375rem', fontSize: '0.875rem', color: '#374151', whiteSpace: 'pre-wrap' }}>
                    {q.answer_explanation && <MathText text={q.answer_explanation} />}
                    {q.explanation_image_url && (
                        <ClickToViewImage src={q.explanation_image_url} containerStyle={{ marginTop: q.answer_explanation ? '0.5rem' : 0 }} />
                    )}
                </div>
            )}
        </div>
    );
}

// A single practice question: the interactive card + a per-question Show/Hide correct answer
// toggle, plus a similarity badge.
function PracticeCard({ q, index }) {
    const [answer, setAnswer] = useState(undefined);
    const [revealed, setRevealed] = useState(false);

    const onOptionSelect = (_qid, key) => setAnswer((cur) => (cur === key ? undefined : key));
    const onMultiToggle = (_qid, key) => setAnswer((cur) => {
        const arr = Array.isArray(cur) ? cur : [];
        return arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key];
    });
    const onBlankSelect = (_qid, bi, ci) => setAnswer((cur) => {
        const blanks = { ...((cur && cur.blanks) || {}) };
        if (blanks[bi] === ci) delete blanks[bi]; else blanks[bi] = ci;
        return { blanks };
    });
    const onTextChange = (_qid, text) => setAnswer(text ? { text } : undefined);

    const pct = q.similarity_score != null ? Math.round(q.similarity_score * 100) : null;

    return (
        <div style={{ border: '1px solid #ede9fe', borderRadius: '0.75rem', padding: '1rem', marginBottom: '1rem', backgroundColor: '#ffffff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, color: '#4c1d95' }}>Practice {index + 1}</span>
                {pct != null ? (
                    <span className="badge" style={{ backgroundColor: '#ede9fe', color: '#5b21b6', fontWeight: 700 }}>{pct}% similar</span>
                ) : (
                    <span className="badge" style={{ backgroundColor: '#f3f4f6', color: '#6b7280', fontWeight: 700 }}>Extra practice</span>
                )}
            </div>

            <QuizQuestionCard
                question={q}
                qIndex={index}
                includePassage={true}
                answer={answer}
                isAnswered={answer !== undefined}
                onOptionSelect={onOptionSelect}
                onMultiToggle={onMultiToggle}
                onBlankSelect={onBlankSelect}
                onTextChange={onTextChange}
            />

            <button
                type="button"
                onClick={() => setRevealed((v) => !v)}
                style={{ marginTop: '0.6rem', backgroundColor: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', borderRadius: '0.375rem', padding: '0.35rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
            >
                {revealed ? 'Hide correct answer' : 'Show correct answer'}
            </button>

            {revealed && <CorrectAnswerReveal q={q} answer={answer} />}
        </div>
    );
}

// Popup launched from Test Submission Details: shows up to 3 questions similar to the reviewed
// one so the user can practice and self-check. Not shown for IELTS/TOEFL (the backend also
// returns an empty list for those levels).
export default function PracticeSimilarQuestionsModal({ sourceQuestionId, onClose }) {
    const CallApi = useApiCaller();
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const res = await CallApi({
                url: getSimilarPracticeQuestionsUrl(sourceQuestionId, 3),
                method: 'GET',
                token: true,
            });
            if (!cancelled) {
                if (res.success) setItems(res.data?.similar || []);
                setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceQuestionId]);

    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1300, backdropFilter: 'blur(1px)', paddingTop: '90px', paddingBottom: '20px' }}>
            <div style={{ backgroundColor: '#ffffff', borderRadius: '0.9rem', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)', width: '86%', maxWidth: 'min(1350px, 88vw)', maxHeight: 'calc(100vh - 110px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb' }}>
                    <h5 style={{ margin: 0, fontWeight: 700, color: '#4c1d95' }}>Practice Similar Questions</h5>
                    <CloseButton onClick={onClose} style={{ position: 'static' }} />
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem' }}>
                    {loading ? (
                        <p style={{ textAlign: 'center', color: '#6b7280' }}>Finding similar questions…</p>
                    ) : items.length === 0 ? (
                        <p style={{ textAlign: 'center', color: '#6b7280' }}>No similar practice questions available.</p>
                    ) : (
                        <>
                            <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>
                                Pick an answer, then reveal the correct one to check yourself. Practice only — not scored.
                            </p>
                            {items.map((q, i) => (
                                <PracticeCard key={q.question_id} q={q} index={i} />
                            ))}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
